import React, { useState, useEffect, useRef } from "react";
import QRCode from "qrcode";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Share2, Copy, MessageCircle, Twitter, Facebook,
  Link as LinkIcon, Check, QrCode, Download,
} from "lucide-react";

const APP_URL = typeof window !== "undefined" ? window.location.origin : "https://thehostelnow.com";
const SHARE_TEXT = "HostelNow — verified student hostels from ₹250/night near every major coaching center in India. Book with Aadhaar KYC in 30 seconds.";

export default function ShareAppButton({ variant = "nav" }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [qrDataUrl, setQrDataUrl] = useState("");
  const qrCanvasRef = useRef(null);

  const url = APP_URL;
  const message = `${SHARE_TEXT} ${url}`;

  // Generate QR whenever dialog opens (also on first mount so it's ready)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const dataUrl = await QRCode.toDataURL(url, {
          width: 512,
          margin: 2,
          errorCorrectionLevel: "H",
          color: { dark: "#9A3412", light: "#FFFFFF" },
        });
        if (!cancelled) setQrDataUrl(dataUrl);
      } catch (err) { console.warn("QR generation failed:", err); }
    })();
    return () => { cancelled = true; };
  }, [url]);

  const nativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: "HostelNow", text: SHARE_TEXT, url });
        return true;
      } catch (err) {
        // User cancelled or share unsupported — log but don't disrupt UX
        if (err?.name !== "AbortError") console.warn("Native share failed:", err);
      }
    }
    return false;
  };

  const openShare = async () => {
    // Try native Web Share on mobile first; fall back to modal
    const ok = await nativeShare();
    if (!ok) setOpen(true);
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(message);
      setCopied(true);
      toast.success("Link copied to clipboard!");
      setTimeout(() => setCopied(false), 2000);
    } catch { toast.error("Couldn't copy"); }
  };

  const downloadQr = () => {
    if (!qrDataUrl) return;
    const a = document.createElement("a");
    a.href = qrDataUrl;
    a.download = "hostelnow-qr.png";
    document.body.appendChild(a);
    a.click();
    a.remove();
    toast.success("QR code downloaded!");
  };

  const shareQr = async () => {
    if (!qrDataUrl) return;
    try {
      const blob = await (await fetch(qrDataUrl)).blob();
      const file = new File([blob], "hostelnow-qr.png", { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: "HostelNow QR Code",
          text: `Scan this to open HostelNow — ${url}`,
        });
        return;
      }
    } catch (err) {
      if (err?.name !== "AbortError") console.warn("QR share failed:", err);
    }
    // Fallback: copy image to clipboard where supported
    try {
      const blob = await (await fetch(qrDataUrl)).blob();
      await navigator.clipboard.write([new ClipboardItem({ "image/png": blob })]);
      toast.success("QR image copied — paste anywhere!");
    } catch {
      downloadQr();
    }
  };

  const shares = [
    {
      key: "whatsapp",
      label: "WhatsApp",
      icon: MessageCircle,
      cls: "bg-emerald-600 hover:bg-emerald-700 text-white",
      href: `https://wa.me/?text=${encodeURIComponent(message)}`,
    },
    {
      key: "twitter",
      label: "Twitter / X",
      icon: Twitter,
      cls: "bg-stone-900 hover:bg-stone-800 text-white",
      href: `https://twitter.com/intent/tweet?text=${encodeURIComponent(SHARE_TEXT)}&url=${encodeURIComponent(url)}`,
    },
    {
      key: "facebook",
      label: "Facebook",
      icon: Facebook,
      cls: "bg-[#1877F2] hover:bg-[#0E5FB8] text-white",
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
    },
    {
      key: "telegram",
      label: "Telegram",
      icon: MessageCircle,
      cls: "bg-[#0088cc] hover:bg-[#006ba1] text-white",
      href: `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(SHARE_TEXT)}`,
    },
  ];

  return (
    <>
      <Button
        data-testid="share-app-btn"
        onClick={openShare}
        variant={variant === "nav" ? "outline" : "default"}
        className={
          variant === "nav"
            ? "rounded-full gap-2 border-orange-200 text-orange-800 hover:bg-orange-50 hover:text-orange-900"
            : "rounded-full gap-2 bg-orange-800 hover:bg-orange-900 text-white"
        }
      >
        <Share2 size={14} /> Share app
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center gap-2">
              <Share2 className="text-orange-800" /> Share HostelNow
            </DialogTitle>
          </DialogHeader>

          {/* QR Code section */}
          <div
            data-testid="share-qr-card"
            ref={qrCanvasRef}
            className="relative rounded-2xl p-5 bg-gradient-to-br from-amber-100 via-orange-100 to-orange-200 border border-orange-200 text-center"
          >
            <div className="inline-flex items-center gap-1 bg-white/80 backdrop-blur px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest text-orange-800">
              <QrCode size={11} /> Scan to open
            </div>
            <div className="mt-3 mx-auto w-44 h-44 bg-white p-3 rounded-2xl shadow-lg border border-orange-200 grid place-items-center">
              {qrDataUrl ? (
                <img
                  data-testid="share-qr-image"
                  src={qrDataUrl}
                  alt="HostelNow QR code"
                  className="w-full h-full object-contain"
                />
              ) : (
                <div className="text-xs text-slate-400">Generating…</div>
              )}
            </div>
            <div className="mt-3 font-display font-black text-lg text-orange-900 tracking-tight">
              Hostel<span className="text-amber-700">Now</span>
            </div>
            <p className="text-[11px] text-orange-900/80 -mt-0.5">Beds from ₹250/night</p>
            <div className="mt-3 flex gap-2 justify-center">
              <Button
                data-testid="qr-download-btn"
                onClick={downloadQr}
                variant="outline"
                size="sm"
                className="rounded-full gap-1.5 bg-white/90 border-orange-300 text-orange-800 hover:bg-white h-8 text-xs"
              >
                <Download size={13} /> Download
              </Button>
              <Button
                data-testid="qr-share-btn"
                onClick={shareQr}
                size="sm"
                className="rounded-full gap-1.5 bg-orange-800 hover:bg-orange-900 text-white h-8 text-xs"
              >
                <Share2 size={13} /> Share QR
              </Button>
            </div>
          </div>

          <div className="mt-3 bg-stone-50 border border-slate-200 rounded-xl p-3">
            <p className="text-[11px] text-slate-500 uppercase tracking-widest font-bold">Or share the message</p>
            <p className="mt-1.5 text-sm text-slate-700 leading-relaxed">{SHARE_TEXT}</p>
            <p className="mt-2 text-xs font-mono text-orange-800 break-all">{url}</p>
          </div>

          <div className="grid grid-cols-2 gap-2 mt-3">
            {shares.map((s) => (
              <a
                key={s.key}
                href={s.href}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`share-${s.key}`}
                className={`flex items-center justify-center gap-2 rounded-full h-11 font-semibold text-sm transition-colors ${s.cls}`}
              >
                <s.icon size={16} /> {s.label}
              </a>
            ))}
          </div>

          <Button
            data-testid="share-copy-link"
            onClick={copy}
            variant="outline"
            className="w-full rounded-full h-11 mt-3 gap-2"
          >
            {copied ? <><Check size={16} className="text-emerald-600" /> Copied!</> : <><LinkIcon size={16} /> Copy invite link</>}
          </Button>

          <p className="text-[11px] text-slate-500 mt-3 text-center">
            Every share helps more students find a safe, affordable bed near their coaching. 🙏
          </p>
        </DialogContent>
      </Dialog>
    </>
  );
}
